import { UploadOutlined } from "@ant-design/icons";
import { useNotification } from "@refinedev/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Avatar,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Table,
  Typography,
} from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";

import { isValidSlug, suggestSlug } from "@hedefuni/domain/blog";

import { api, ApiError } from "../api";
import { uploadBlogImage } from "../components/blog-image";
import type { BlogAuthor } from "../types";

type Values = {
  kind: "PERSON" | "ORGANIZATION";
  name: string;
  slug: string;
  jobTitle: string;
  bio: string;
};

const slugRule = {
  validator: (_: unknown, value: string) =>
    !value || isValidSlug(value)
      ? Promise.resolve()
      : Promise.reject(new Error("Sadece küçük harf, rakam ve tek tire.")),
};

/**
 * /blog/authors (permission `blog.manage`): the people posts are written by. Their name, title,
 * bio and photo appear on every post and on their own page (hedefuni.com/blog/yazar/...), which
 * gives posts the expertise signals search engines look for (E-E-A-T).
 */
export function BlogAuthorsPage() {
  const navigate = useNavigate();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Values>();
  const [editing, setEditing] = useState<BlogAuthor | "new" | null>(null);
  const [photo, setPhoto] = useState<{ key: string; url: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const { data: authors, isLoading } = useQuery({
    queryKey: ["office", "blog", "authors"],
    queryFn: () => api<BlogAuthor[]>("/api/office/blog/authors"),
  });

  function edit(author: BlogAuthor | "new") {
    setEditing(author);
    if (author === "new") {
      form.resetFields();
      setPhoto(null);
    } else {
      form.setFieldsValue({
        kind: author.kind,
        name: author.name,
        slug: author.slug,
        jobTitle: author.jobTitle ?? "",
        bio: author.bio,
      });
      setPhoto(
        author.photoKey && author.photoUrl ? { key: author.photoKey, url: author.photoUrl } : null,
      );
    }
  }

  async function save() {
    const values = await form.validateFields();
    setSaving(true);
    const body = { ...values, jobTitle: values.jobTitle ?? "", photoKey: photo?.key ?? null };
    try {
      if (editing === "new") await api("/api/office/blog/authors", { method: "POST", body });
      else if (editing) {
        await api(`/api/office/blog/authors/${editing.id}`, { method: "PUT", body });
      }
      await queryClient.invalidateQueries({ queryKey: ["office", "blog"] });
      setEditing(null);
    } catch (cause) {
      open?.({
        type: "error",
        message: cause instanceof ApiError ? cause.message : "Kaydedilemedi.",
      });
    } finally {
      setSaving(false);
    }
  }

  async function remove(author: BlogAuthor) {
    try {
      await api(`/api/office/blog/authors/${author.id}`, { method: "DELETE" });
      await queryClient.invalidateQueries({ queryKey: ["office", "blog"] });
    } catch (cause) {
      open?.({ type: "error", message: cause instanceof ApiError ? cause.message : "Silinemedi." });
    }
  }

  async function onPhoto(file: File | undefined) {
    if (!file) return;
    try {
      setPhoto(await uploadBlogImage(file));
    } catch (cause) {
      open?.({ type: "error", message: cause instanceof Error ? cause.message : "Yüklenemedi." });
    }
  }

  return (
    <Card
      title="Blog yazarları"
      extra={
        <Space>
          <Button onClick={() => navigate("/blog")}>Yazılara dön</Button>
          <Button type="primary" onClick={() => edit("new")}>
            Yeni yazar
          </Button>
        </Space>
      }
    >
      <Table<BlogAuthor>
        rowKey="id"
        loading={isLoading}
        dataSource={authors}
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: "Henüz yazar yok. Yazı eklemeden önce bir yazar ekle." }}
        columns={[
          {
            title: "Yazar",
            render: (_, author) => (
              <Space>
                <Avatar src={author.photoUrl ?? undefined}>{author.name.charAt(0)}</Avatar>
                <Space direction="vertical" size={0}>
                  <Typography.Text strong>{author.name}</Typography.Text>
                  <Typography.Text type="secondary">{author.jobTitle ?? ""}</Typography.Text>
                </Space>
              </Space>
            ),
          },
          { title: "Adres", render: (_, author) => `/blog/yazar/${author.slug}` },
          { title: "Yazı", dataIndex: "postCount" },
          {
            title: "",
            render: (_, author) => (
              <Space>
                <Button size="small" onClick={() => edit(author)}>
                  Düzenle
                </Button>
                <Popconfirm
                  title="Yazar silinsin mi?"
                  description={author.postCount > 0 ? "Yazısı olan yazar silinemez." : undefined}
                  okText="Sil"
                  cancelText="Vazgeç"
                  okButtonProps={{ danger: true, disabled: author.postCount > 0 }}
                  onConfirm={() => remove(author)}
                >
                  <Button size="small" danger>
                    Sil
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        title={editing === "new" ? "Yeni yazar" : "Yazarı düzenle"}
        open={editing !== null}
        okText="Kaydet"
        cancelText="Vazgeç"
        confirmLoading={saving}
        onOk={save}
        onCancel={() => setEditing(null)}
        destroyOnHidden
        forceRender
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ kind: "PERSON" }}
          onValuesChange={(changed: Partial<Values>) => {
            if (editing === "new" && "name" in changed) {
              form.setFieldValue("slug", suggestSlug(changed.name ?? ""));
            }
          }}
        >
          <Form.Item
            name="kind"
            label="Tür"
            extra="Kurum: bir ekip adına yazılan yazılar (ör. HedefUni Ekibi). Arama motorlarına kişi yerine kurum olarak bildirilir."
          >
            <Radio.Group
              optionType="button"
              options={[
                { label: "Kişi", value: "PERSON" },
                { label: "Kurum", value: "ORGANIZATION" },
              ]}
            />
          </Form.Item>
          <Form.Item name="name" label="Ad" rules={[{ required: true, message: "Adı yaz." }]}>
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item
            name="slug"
            label="Adres (slug)"
            rules={[{ required: true, message: "Adresi yaz." }, slugRule]}
          >
            <Input addonBefore="/blog/yazar/" maxLength={80} />
          </Form.Item>
          <Form.Item
            name="jobTitle"
            label="Unvan"
            extra="Ör. Matematik Öğretmeni, Rehber Öğretmen; kurumda ör. Eğitim içerik ekibi"
          >
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item
            name="bio"
            label="Biyografi"
            rules={[{ required: true, message: "Biyografiyi yaz." }]}
            extra="Deneyimini ve uzmanlığını anlatan birkaç cümle."
          >
            <Input.TextArea maxLength={2000} showCount autoSize={{ minRows: 4 }} />
          </Form.Item>
          <Space>
            <Avatar size={56} src={photo?.url}>
              ?
            </Avatar>
            <Button
              icon={<UploadOutlined />}
              onClick={() => document.getElementById("blog-author-photo")?.click()}
            >
              Fotoğraf yükle
            </Button>
            {photo ? <Button onClick={() => setPhoto(null)}>Kaldır</Button> : null}
          </Space>
          <input
            id="blog-author-photo"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(event) => onPhoto(event.target.files?.[0])}
          />
        </Form>
      </Modal>
    </Card>
  );
}
